import {Component, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {UserResponse} from "../interface/userResponse";
import {UserService} from "../service/user.service";
import {MenuItem} from "../interface/menuItem";
import {NavigationEnd, Router} from "@angular/router";
import {ActiveTeamService} from "../service/active-team.service";
import {BehaviorSubject, catchError, concatMap, filter, of, Subscription} from "rxjs";
import {TeamService} from "../service/team.service";
import {Team} from "../interface/team";
import {HttpErrorResponse} from "@angular/common/http";
import {SharedTeamNameChangeService} from "../service/team-name-change.service";
import {UserRole} from "../enums/UserRole";
import {Language} from "../models/language";
import {LANG} from "../constants/local-storage.contant";
import {CookieUtils} from "../utils/cookie.utils";
import {TranslateService} from "@ngx-translate/core";
import {LanguageService} from "../service/language.service";
import {SharedConstants} from "../utilityClass/sharedConstants";
import {ReservedQrStateService} from "../service/reserved-qr-state-service";
import {FeeService} from "../service/fee.service";
import {FeeMembershipResponse, UserIndustryResponse} from "../interface/fee.interface";
import {SubscriptionService} from "../service/subscription.service";
import {SubscriptionType} from "../enums/subscription-type";
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import {
  FeeIndustryDeleteDialogComponent,
  FeeIndustryDeleteDialogData
} from '../fee-industry-delete-dialog/fee-industry-delete-dialog.component';

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.scss'
})
export class SidebarComponent implements OnInit, OnDestroy {

  @Input() user: UserResponse | any
  @Input() isOpened: boolean = false;

  @Output() cancelProfileEdit = new EventEmitter<void>();
  @Output() menuClosed = new EventEmitter<void>();

  private readonly SHOW_ADMIN_SHOP_MENU_URL = "admin/page/shop";
  private readonly ADMIN_SHOP_PRODUCTS_URL = "admin/page/shop/products";
  private readonly SHOW_ADMIN_ACTIVITY_MENU_URL = "admin/page/activity";
  private readonly ADMIN_COMMISSIONS_URL = "admin/page/commissions";
  protected readonly Language = Language;

  isAdmin: boolean = false;

  showTeamList: boolean = false;
  showTeamListDistance: boolean = false;
  activeTeamIndex: number | null = null;

  subscription: Subscription | any;
  nameChangeSubscription: Subscription | any;
  languageSubscription: Subscription | any;
  reservedQrSubscription: Subscription | any;

  userCreatedTeam: Team | any = null;
  teams: Team[] | any;

  isAnimationEnabled: boolean = false;
  moveSidebarToRight: boolean = false;

  reservedId: string | null = null;

  shopMenuItemsMap: { [key: string]: boolean; } = {
    ['Products']: true,
    ['Orders']: false
  }

  activityMenuItemsMap: { [key: string]: boolean; } = {
    ['Activity']: true,
    ['Commissions']: false
  }

  showFeeMenu: boolean = false;
  hasPremiumSubscription: boolean = false;
  userIndustries: UserIndustryResponse[] = [];
  feeLogoUrls: Map<number, string | null> = new Map();
  myMemberships: FeeMembershipResponse[] = [];
  openedIndustryMenuId: number | null = null;

  protected lang: string = '';
  protected isLanguageChangeOpened: boolean = false;
  protected isWithLowerOrEquals800: boolean = false;

  menuItems: MenuItem[] = [
    {id: 'blog', routerLink: '#', text: 'Contacts'},
    {id: 'faqs', routerLink: '#', text: 'Social'},
    {id: 'payment', routerLink: '#', text: 'Payment'}
  ];

  public static initializer: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public static activeTeamInitializer: BehaviorSubject<number | null> = new BehaviorSubject<number | null>(null);

  constructor(
    private userService: UserService,
    private router: Router,
    private activeTeamService: ActiveTeamService,
    private teamService: TeamService,
    private sharedService: SharedTeamNameChangeService,
    private reservedQrStateService: ReservedQrStateService,
    private translateService: TranslateService,
    private languageService: LanguageService,
    private feeService: FeeService,
    private dialog: MatDialog,
    private subscriptionService: SubscriptionService
  ) {
    SidebarComponent.initializer.subscribe({
      next: (value: boolean) => {
        if (value) {
          this.initialize();
        }
      }
    });
    SidebarComponent.activeTeamInitializer.subscribe({
      next: (value: number | null) => {
        if (value) {
          this.activeTeamIndex = value;
        }
      }
    })
    this.subscribeToRouterEvents();
    this.checkScreenSize(window.innerWidth);
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe({
      next: () => {
        this.closeMenu();
        if (this.router.url.startsWith('/fees')) {
          this.initializeFeeMenu();
        }
      }
    });
  }

  ngOnInit(): void {
    this.reservedQrSubscription = ReservedQrStateService.reservedId$.subscribe({
      next: (id: string | null) => {
        this.reservedId = id;
      }
    });
    this.initialize();
    this.checkIfTeamRoute();
    this.initializeShopMenu();
    this.initializeActivityMenu();
    this.initializeFeeMenu();

    this.lang = this.languageService.getCurrentLanguage();

    this.languageSubscription = this.languageService.currentLanguage$.subscribe({
      next: (lang: string) => {
        this.lang = lang;
      }
    });
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    if (this.nameChangeSubscription) {
      this.nameChangeSubscription.unsubscribe();
    }
    if (this.languageSubscription) {
      this.languageSubscription.unsubscribe();
    }
    if (this.reservedQrSubscription) {
      this.reservedQrSubscription.unsubscribe();
    }
  }

  isCurrentLang(lang: Language) {
    const verifiedCurrentLang = CookieUtils.getCookie(LANG) || Language.HY;
    return verifiedCurrentLang === lang;
  }

  changeLang(lang: string) {
    this.languageService.setLanguage(lang);
    this.isLanguageChangeOpened = false;
    this.closeMenu();
  }



  navigateToWithReservedId(route: string): void {
    this.router.navigate([route], {
      state: {
        reservedIdentificationId: this.reservedId
      }
    });
  }

  get isShopProductsActive() {
    return this.shopMenuItemsMap['Products'].valueOf();
  }

  get isShopOrdersActive() {
    return this.shopMenuItemsMap['Orders'].valueOf();
  }

  get showShopMenu(): boolean {
    return this.router.url.includes(this.SHOW_ADMIN_SHOP_MENU_URL);
  }

  get showShopMenuProducts(): boolean {
    return this.router.url.includes(this.ADMIN_SHOP_PRODUCTS_URL);
  }

  initializeShopMenu() {
    if (this.showShopMenu) {
      this.showShopMenuProducts ? this.setShopProductsActive() : this.setShopOrdersActive();
    }
  }

  setShopOrdersActive() {
    this.shopMenuItemsMap['Orders'] = true;
    this.shopMenuItemsMap['Products'] = false;
  }

  setShopProductsActive() {
    this.shopMenuItemsMap['Products'] = true;
    this.shopMenuItemsMap['Orders'] = false;
  }

  get showActivityMenu(): boolean {
    return this.router.url.includes(this.SHOW_ADMIN_ACTIVITY_MENU_URL) || this.router.url.includes(this.ADMIN_COMMISSIONS_URL);
  }

  get isActivityPageActive(): boolean {
    return this.activityMenuItemsMap['Activity'].valueOf();
  }

  get isCommissionsActive(): boolean {
    return this.activityMenuItemsMap['Commissions'].valueOf();
  }

  initializeActivityMenu() {
    if (this.showActivityMenu) {
      this.router.url.includes(this.ADMIN_COMMISSIONS_URL) ? this.setCommissionsActive() : this.setActivityPageActive();
    }
  }

  setActivityPageActive() {
    this.activityMenuItemsMap['Activity'] = true;
    this.activityMenuItemsMap['Commissions'] = false;
  }

  setCommissionsActive() {
    this.activityMenuItemsMap['Commissions'] = true;
    this.activityMenuItemsMap['Activity'] = false;
  }

  loadPremiumStatus() {
    if (!this.isAdmin) {
      this.subscriptionService.getActiveIndividualSubscription().subscribe({
        next: (sub) => {
          this.hasPremiumSubscription =
            sub?.subscriptionInfoResponse?.subscriptionType === SubscriptionType.PREMIUM &&
            !sub?.isExpired;
        }
      });
    }
  }

  initializeFeeMenu() {
    if (!this.isAdmin) {
      this.feeService.getUserIndustries().subscribe({
        next: (groups) => {
          this.userIndustries = groups;
          this.feeLogoUrls = new Map(
            groups.map(g => [g.id, this.feeService.getLogoUrl(g.logoPath)])
          );
          if (this.router.url.includes('fees')) {
            this.showFeeMenu = true;
          }
        }
      });
      this.feeService.getMyMemberships().subscribe({
        next: (memberships) => {
          this.myMemberships = memberships;
        }
      });
    }
  }

  toggleFeeMenu(event: Event) {
    event.stopPropagation();
    this.showFeeMenu = !this.showFeeMenu;
  }

  toggleIndustryMenu(event: Event, industryId: number): void {
    event.stopPropagation();
    this.openedIndustryMenuId = this.openedIndustryMenuId === industryId ? null : industryId;
  }

  deleteIndustry(event: Event, industryId: number): void {
    event.stopPropagation();
    this.closeMenu();
    const selected = this.userIndustries.find(i => i.id === industryId);
    const config = new MatDialogConfig<FeeIndustryDeleteDialogData>();
    config.width = '440px';
    config.panelClass = 'industry-delete-panel';
    config.data = {
      industryId,
      companyName: selected?.companyName || 'this industry'
    };
    this.dialog.open(FeeIndustryDeleteDialogComponent, config)
      .afterClosed()
      .subscribe((deleted: boolean | undefined) => {
        if (!deleted) {
          return;
        }
        const onFeesRoute = this.router.url.includes('/fees');
        const wasViewingDeletedIndustry = this.isViewingIndustry(industryId);
        const hasRemainingFees = this.userIndustries.some(i => i.id !== industryId)
          || this.myMemberships.some(m => m.industryId !== industryId);
        this.initializeFeeMenu();

        if (onFeesRoute && !hasRemainingFees) {
          this.router.navigate(['/fees'], {queryParams: {create: 1}});
        } else if (wasViewingDeletedIndustry) {
          this.router.navigate(['/fees']);
        }
      });
  }

  private isViewingIndustry(industryId: number): boolean {
    if (this.router.url.includes(`/fees/${industryId}`)) {
      return true;
    }
    const membershipId = Number(this.router.parseUrl(this.router.url).queryParams['membershipId']);
    if (Number.isNaN(membershipId)) {
      return false;
    }
    const membership = this.myMemberships.find(m => m.memberId === membershipId);
    return !!membership && membership.industryId === industryId;
  }

  navigateToFees(event: Event) {
    event.stopPropagation();
    this.router.navigate(['/fees']);
  }

  navigateToFeeCreate(event: Event): void {
    event.stopPropagation();
    this.router.navigate(['/fees'], { queryParams: { create: 1 } });
  }

  navigateToFeeMembership(event: Event, memberId: number): void {
    event.stopPropagation();
    this.router.navigate(['/fees'], { queryParams: { membershipId: memberId } });
  }

  isActiveFeeMembership(memberId: number): boolean {
    if (!this.router.url.includes('/fees')) {
      return false;
    }
    const membershipId = Number(this.router.parseUrl(this.router.url).queryParams['membershipId']);
    return !Number.isNaN(membershipId) && membershipId === memberId;
  }

  closeMenu() {
    this.isOpened = false;
    this.openedIndustryMenuId = null;
    this.menuClosed.emit();
  }

  isActive(route: string) {
    return this.router.url.includes(route);
  }

  isActiveStrict(route: string) {
    return this.router.url == (route);
  }

  showCards(): void {
    this.showTeamList = false;
    this.showTeamListDistance = false;
  }

  checkIfTeamRoute() {
    const currentRoute = this.router.url;
    if (currentRoute.includes('teams')) {
      this.showTeamList = true;
      this.showTeamListDistance = true;
    }
  }

  showProfileEdit() {
    this.showTeamList = !this.showTeamList;
    this.showTeamListDistance = !this.showTeamListDistance;
    this.cancelProfileEdit.emit();
  }

  setActiveTeam(index: number) {
    if (this.activeTeamIndex === index) {
      return;
    }
    this.activeTeamService.setActiveTeamIndex(index);
    this.activeTeamIndex = index;
    const currentRoute = this.router.url;
    if (currentRoute !== 'teams') {
      this.router.navigate(['/teams']);
    }
    this.router.isActive('teams', true);
  }

  updateTeam(index: number) {
    localStorage.setItem(SharedConstants.ACTIVE_TEAM_INDEX, index.toString());
  }

  deleteActiveTeamIndex() {
    localStorage.removeItem(SharedConstants.ACTIVE_TEAM_INDEX)
  }

  public initialize() {
    this.isAdmin = this.userService.role === UserRole.ADMIN;
    this.loadPremiumStatus();
    this.subscription = this.activeTeamService.activeTeamIndex$.subscribe({
      next: (index: number | null) => {
        this.activeTeamIndex = index;
      }
    });
    this.nameChangeSubscription = this.sharedService.teamName$.subscribe({
      next: (name: string | null) => {
        if (this.userCreatedTeam && name) {
          this.userCreatedTeam.name = name;
        }
      }
    });
    this.initTeam();
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: Event) {
    const target = event.target as Window;
    this.checkScreenSize(target.innerWidth);
  }

  checkScreenSize(width: number) {
    this.isWithLowerOrEquals800 = width <= 800;

    if (this.isWithLowerOrEquals800) {
      this.isAnimationEnabled = true;
      this.setMoveSidebarToRight = true;
    } else {
      this.isAnimationEnabled = false;
      this.setMoveSidebarToRight = false;
      this.isLanguageChangeOpened = false;
      this.closeMenu();
    }
  }

  set setMoveSidebarToRight(value: boolean) {
    this.moveSidebarToRight = value;
  }

  public getTranslatedTeamName(teamName: string): string {
    return teamName === SharedConstants.MY_TEAM
      ? this.translateService.instant('my_team.my_team')
      : teamName;
  }

  private initTeam() {
    this.getTeamsForCurrentUserAsMember().pipe(
      concatMap(() => this.getCurrentUserTeam())
    ).subscribe();
  }

  private getTeamsForCurrentUserAsMember() {
    return this.teamService.getTeamsByCurrentMember().pipe(
      concatMap(teams => {
        this.teams = teams;
        return of(null);
      })
    );
  }

  private getCurrentUserTeam() {
    return this.teamService.getCurrentUserTeam().pipe(
      concatMap(team => {
        this.userCreatedTeam = team;
        return of(null);
      }),
      catchError((error: HttpErrorResponse) => {
        this.userCreatedTeam = null;
        return of(null);
      })
    );
  }

  private subscribeToRouterEvents(): void {
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe({
      next: () => {
        if (!this.router.url.includes('teams')) {
          this.showTeamList = false;
        }
      }
    });
  }
}
